import { useCallback, useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import type { SortEnd } from 'react-sortable-hoc';
import { useStore } from 'effector-react';
import type { SceneCompareConfig, SceneCompareItem, SceneData } from '@types';
import Button from '@components/Button';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import Title from '@components/Title';
import IconVip from '@components/icons/IconVip';
import SidebarHeader from '@modules/sidebar/components/SidebarHeader';
import { showVipModal } from '@utils/showVipModal';
import useToggle from '@hooks/useToggle';
import IconSettings from '@components/icons/IconSettings';
import SortableList from '@components/SortableList';
import { $sceneMapApi } from '@stores/dataStores';
import arrayMove from '@utils/arrayMove';
import { COMPARE_ITEM_DEFAULT, COMPARE_MAX } from '../constants';
import { PanelCompare, type PanelCompareProps } from '../PanelCompare';
import { CompareItem } from './CompareItem';
import { generateCompareItemId } from '../utils';
import { $compareSelected, $compareSelectedApi } from '../compareStores';

interface Props {
  scene: SceneData;
  onSettingClick: () => void;
}

export function CompareList({ scene, onSettingClick }: Props) {
  const list = scene.compareConfig?.scenes ?? [];
  const selected = useStore($compareSelected);

  const isMax = list.length >= COMPARE_MAX;
  const loading = useToggle(false);
  const [editing, setEditing] = useState<{
    data: SceneCompareItem;
    isNew: boolean;
  } | null>(null);

  useEffect(() => {
    if (list[0]) {
      $compareSelectedApi.set(list[0].id);
    }
  }, []);

  const handleAddClick = () => {
    showVipModal('720_vip_compare');
    loading.on();
    setEditing({
      data: {
        ...COMPARE_ITEM_DEFAULT,
        id: generateCompareItemId(),
        type: scene.type,
      },
      isNew: true,
    });
    loading.off();
  };

  const handleSortEnd = (sort: SortEnd) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.compareConfig ??= {} as SceneCompareConfig;
        draft.compareConfig.scenes ??= [];
        draft.compareConfig.scenes = arrayMove({
          array: draft.compareConfig.scenes,
          fromIndex: sort.oldIndex,
          toIndex: sort.newIndex,
        });
      },
    });
  };

  const handleItemClick = useCallback((item: SceneCompareItem) => {
    setEditing({
      data: { ...COMPARE_ITEM_DEFAULT, ...item },
      isNew: false,
    });
  }, []);

  const handleDelete = useCallback(
    (id: SceneCompareItem['id'], options: { isSelected: boolean }) => {
      if (options.isSelected) {
        const rest = list.filter((x) => x.id !== id);
        $compareSelectedApi.set(rest[rest.length - 1]?.id ?? null);
      }

      $sceneMapApi.updateScene({
        id: scene.id,
        updater: (draft) => {
          draft.compareConfig ??= {} as SceneCompareConfig;
          draft.compareConfig.scenes ??= [];
          draft.compareConfig.scenes = draft.compareConfig.scenes.filter(
            (x) => x.id !== id
          );
        },
      });
    },
    []
  );

  const handleEditConfirm: PanelCompareProps['onConfirm'] = (payload) => {
    const { data, isNew } = payload;
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.compareConfig ??= {} as SceneCompareConfig;
        draft.compareConfig.scenes ??= [];
        if (isNew) {
          draft.compareConfig.scenes.push(data);
        } else {
          const index = draft.compareConfig.scenes.findIndex(
            (x) => x.id === data.id
          );
          if (index !== -1) {
            draft.compareConfig.scenes[index] = data;
          }
        }
      },
    });
    if (isNew) {
      $compareSelectedApi.set(data.id);
    }
  };

  const renderList = () => {
    return (
      <div className="relative flex-1">
        <SortableList
          axis="y"
          lockAxis="y"
          distance={10}
          onSortEnd={handleSortEnd}
        >
          <Scrollbars>
            {list.map((x, i) => {
              return (
                <CompareItem
                  key={x.id}
                  index={i}
                  data={x}
                  isSelected={x.id === selected}
                  onEditClick={handleItemClick}
                  onDeleteClick={handleDelete}
                />
              );
            })}
          </Scrollbars>
        </SortableList>
      </div>
    );
  };

  const renderEmpty = () => {
    return (
      <div className="flex-1">
        <Tip className="mt-5 text-center text-base">
          {`每个场景最多可添加${COMPARE_MAX}个对比场景`}
        </Tip>
        <Tip className="mt-5 text-center text-base">
          添加后支持上下拖拽调整场景顺序
        </Tip>
      </div>
    );
  };

  return (
    <>
      <SidebarHeader
        title={
          <div className="flex items-center">
            <Title>场景对比</Title>
            <IconVip className="ml-5" />
            <Tip className="ml-5 text-base">
              {`${list.length}/${COMPARE_MAX}`}
            </Tip>
          </div>
        }
        btns={
          <TipWrapper text="对比设置" placement="bottom" size="sm">
            <IconSettings
              className="focus:outline-none"
              onClick={onSettingClick}
            />
          </TipWrapper>
        }
      />
      <Button
        size="lg"
        className="mb-10 w-full"
        disabled={loading.isOn || isMax}
        onClick={handleAddClick}
      >
        添加对比场景
      </Button>
      {list.length > 0 ? renderList() : renderEmpty()}
      {editing && (
        <PanelCompare
          data={editing.data}
          isNew={editing.isNew}
          currentSceneType={scene.type}
          onChange={(data) => {
            setEditing((prev) => {
              if (!prev) return prev;
              return { ...prev, data };
            });
          }}
          onConfirm={handleEditConfirm}
          onDelete={(id) => handleDelete(id, { isSelected: true })}
          onRequestClose={() => {
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

export default CompareList;
